import React, { useState, useEffect, useContext, useRef } from 'react';
import { auroraTopbarSx } from '../theme/auroraSurface';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Alert,
  Snackbar,
  Badge,
  Select,
  FormControl,
  InputLabel,
  LinearProgress,
  Divider,
  Stack,
  Autocomplete,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  AppBar,
  Toolbar,
  Menu,
  CssBaseline,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard,
  Groups,
  School,
  Assignment,
  Settings,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  TrendingUp,
  People,
  EventNote,
  Visibility,
  AttachMoney,
  CalendarToday,
  LocationOn,
  Phone,
  Email,
  PersonAdd,
  ContactPhone,
  Block,
  Schedule,
  YouTube,
  PhotoLibrary,
  CloudUpload,
  Logout,
  AccountCircle,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import { INDIAN_CITIES, COURSE_OPTIONS } from '../utils/indianCities';
import { GRADIENTS, RADIUS } from '../theme/tokens';

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

/**
 * Institute Manager Dashboard - Complete management interface
 */
function InstituteManagerDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [currentView, setCurrentView] = useState('dashboard');
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Dashboard Data
  const [dashboardData, setDashboardData] = useState(null);
  const [institute, setInstitute] = useState(null);
  const [batches, setBatches] = useState([]);
  const [leads, setLeads] = useState([]);
  const [plans, setPlans] = useState([]);

  // Dialog States
  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [editBatchOpen, setEditBatchOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [createPlanOpen, setCreatePlanOpen] = useState(false);
  const [editPlanOpen, setEditPlanOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [leadDetailOpen, setLeadDetailOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editInstituteOpen, setEditInstituteOpen] = useState(false);

  // Form States
  const [newBatch, setNewBatch] = useState({
    name: '',
    course_type: '',
    start_date: '',
    end_date: '',
    timings: '',
    days: [],
    location: '',
    mode: 'offline',
    max_students: '',
    faculty_name: '',
    price_cents: '',
    registration_deadline: '',
    video_url: '',
  });

  const [editBatchData, setEditBatchData] = useState({});

  const [newPlan, setNewPlan] = useState({
    name: '',
    description: '',
    course_type: '',
    duration_months: '',
    price_cents: '',
    batch_size: '',
    mode: 'offline',
    features: [],
  });

  const [editPlanData, setEditPlanData] = useState({});
  const [editInstituteData, setEditInstituteData] = useState({});

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Gallery state
  const [gallery, setGallery] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef(null);

  // Menu Items
  const menuItems = [
    { id: 'dashboard', text: 'Dashboard', icon: <Dashboard /> },
    { id: 'batches', text: 'Batches', icon: <Groups /> },
    { id: 'leads', text: 'Student Queries', icon: <Assignment /> },
    { id: 'plans', text: 'Course Plans', icon: <School /> },
    { id: 'settings', text: 'Institute Settings', icon: <Settings /> },
  ];

  // Use COURSE_OPTIONS from indianCities.js for consistency
  const leadStatuses = ['new', 'contacted', 'interested', 'not_interested', 'enrolled', 'cancelled'];
  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  // Fetch Dashboard Data
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashRes, batchesRes, leadsRes, plansRes, instituteRes] = await Promise.all([
        api.get('/institute-manager/dashboard'),
        api.get('/institute-manager/batches'),
        api.get('/institute-manager/leads'),
        api.get('/institute-manager/plans'),
        api.get('/institute-manager/institute'),
      ]);

      setDashboardData(dashRes.data.data);
      setBatches(batchesRes.data.data.batches || []);
      setLeads(leadsRes.data.data.leads || []);
      setPlans(plansRes.data.data.plans || []);
      const inst = instituteRes.data.data.institute || null;
      setInstitute(inst);
      setGallery(inst?.gallery || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      showSnackbar('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleMenuClick = (itemId) => {
    setCurrentView(itemId);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  // User menu handlers
  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate('/login');
  };

  // Batch Handlers
  const handleCreateBatch = async () => {
    try {
      if (!newBatch.name || !newBatch.course_type || !newBatch.start_date) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      await api.post('/institute-manager/batches', {
        ...newBatch,
        max_students: newBatch.max_students ? parseInt(newBatch.max_students) : null,
        price_cents: newBatch.price_cents ? parseInt(newBatch.price_cents) : null,
        video_url: newBatch.video_url || null,
      });

      setCreateBatchOpen(false);
      setNewBatch({
        name: '', course_type: '', start_date: '', end_date: '',
        timings: '', days: [], location: '', mode: 'offline',
        max_students: '', faculty_name: '', price_cents: '', registration_deadline: '',
        video_url: '',
      });
      showSnackbar('Batch created successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create batch', 'error');
    }
  };

  const handleEditBatch = async () => {
    try {
      await api.put(`/institute-manager/batches/${selectedBatch.id}`, {
        ...editBatchData,
        max_students: editBatchData.max_students ? parseInt(editBatchData.max_students) : null,
        price_cents: editBatchData.price_cents ? parseInt(editBatchData.price_cents) : null,
        video_url: editBatchData.video_url || null,
      });

      setEditBatchOpen(false);
      setSelectedBatch(null);
      showSnackbar('Batch updated successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update batch', 'error');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this batch?')) return;

    try {
      await api.delete(`/institute-manager/batches/${batchId}`);
      showSnackbar('Batch deleted successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete batch', 'error');
    }
  };

  const openEditBatchDialog = (batch) => {
    setSelectedBatch(batch);
    setEditBatchData({
      name: batch.name,
      course_type: batch.course_type,
      start_date: batch.start_date?.split('T')[0] || '',
      end_date: batch.end_date?.split('T')[0] || '',
      timings: batch.timings || '',
      days: batch.days || [],
      location: batch.location || '',
      mode: batch.mode || 'offline',
      max_students: batch.max_students || '',
      faculty_name: batch.faculty_name || '',
      price_cents: batch.price_cents || '',
      registration_deadline: batch.registration_deadline?.split('T')[0] || '',
      video_url: batch.video_url || '',
    });
    setEditBatchOpen(true);
  };

  // Plan Handlers
  const handleCreatePlan = async () => {
    try {
      if (!newPlan.name || !newPlan.course_type || !newPlan.price_cents) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      await api.post('/institute-manager/plans', {
        ...newPlan,
        duration_months: newPlan.duration_months ? parseInt(newPlan.duration_months) : null,
        price_cents: parseInt(newPlan.price_cents),
        batch_size: newPlan.batch_size ? parseInt(newPlan.batch_size) : null,
      });

      setCreatePlanOpen(false);
      setNewPlan({
        name: '', description: '', course_type: '',
        duration_months: '', price_cents: '', batch_size: '',
        mode: 'offline', features: [],
      });
      showSnackbar('Plan created successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to create plan', 'error');
    }
  };

  const handleEditPlan = async () => {
    try {
      await api.put(`/institute-manager/plans/${selectedPlan.id}`, {
        ...editPlanData,
        duration_months: editPlanData.duration_months ? parseInt(editPlanData.duration_months) : null,
        price_cents: editPlanData.price_cents ? parseInt(editPlanData.price_cents) : null,
        batch_size: editPlanData.batch_size ? parseInt(editPlanData.batch_size) : null,
      });

      setEditPlanOpen(false);
      setSelectedPlan(null);
      showSnackbar('Plan updated successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update plan', 'error');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;

    try {
      await api.delete(`/institute-manager/plans/${planId}`);
      showSnackbar('Plan deleted successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete plan', 'error');
    }
  };

  const openEditPlanDialog = (plan) => {
    setSelectedPlan(plan);
    setEditPlanData({
      name: plan.name,
      description: plan.description || '',
      course_type: plan.course_type,
      duration_months: plan.duration_months || '',
      price_cents: plan.price_cents || '',
      batch_size: plan.batch_size || '',
      mode: plan.mode || 'offline',
      features: plan.features || [],
    });
    setEditPlanOpen(true);
  };

  // Lead Handlers
  const handleUpdateLead = async (leadId, updates) => {
    try {
      await api.put(`/institute-manager/leads/${leadId}`, updates);
      showSnackbar('Lead updated successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update lead', 'error');
    }
  };

  const openLeadDetail = async (lead) => {
    try {
      const response = await api.get(`/institute-manager/leads/${lead.id}`);
      setSelectedLead(response.data.data.lead);
      setLeadDetailOpen(true);
    } catch (err) {
      showSnackbar('Failed to load lead details', 'error');
    }
  };

  // Institute Settings Handlers
  const handleUpdateInstitute = async () => {
    try {
      await api.put('/institute-manager/institute', {
        ...editInstituteData,
        lat: editInstituteData.lat ? parseFloat(editInstituteData.lat) : null,
        lng: editInstituteData.lng ? parseFloat(editInstituteData.lng) : null,
        established_year: editInstituteData.established_year ? parseInt(editInstituteData.established_year) : null,
      });

      setEditInstituteOpen(false);
      showSnackbar('Institute updated successfully');
      fetchDashboardData();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update institute', 'error');
    }
  };

  // Image Gallery Handlers
  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('images', files[i]);
      }

      const response = await api.post('/institute-manager/institute/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setGallery(response.data.data.gallery);
      showSnackbar(`${files.length} image(s) uploaded successfully!`);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to upload images', 'error');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await api.delete('/institute-manager/institute/images', {
        data: { imageUrl },
      });
      setGallery(response.data.data.gallery);
      showSnackbar('Image deleted successfully!');
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to delete image', 'error');
    }
  };

  const getImageUrl = (path) => {
    if (!path) return '';
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${baseUrl}${path}`;
  };

  const openEditInstituteDialog = () => {
    if (institute) {
      setEditInstituteData({
        name: institute.name,
        description: institute.description || '',
        address: institute.address,
        city: institute.city || '',
        lat: institute.lat || '',
        lng: institute.lng || '',
        gallery: institute.gallery || [],
        facilities: institute.facilities || [],
        courses_offered: institute.courses_offered || [],
        contact_email: institute.contact_email || '',
        contact_phone: institute.contact_phone || '',
        website: institute.website || '',
        established_year: institute.established_year || '',
      });
      setEditInstituteOpen(true);
    }
  };

  // Render Dashboard Overview
  const renderDashboard = () => {
    if (!dashboardData) return <LinearProgress />;

    const stats = dashboardData.leadStats || {};
    const totalLeads = Object.values(stats).reduce((sum, val) => sum + val, 0);

    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Box mb={4}>
          <Typography variant="h4" fontWeight="800" gutterBottom>
            Institute Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.name}! Manage your institute from here.
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              whileHover={{ scale: 1.05 }}
              sx={{
                background: GRADIENTS.brand,
                color: 'white',
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Leads
                    </Typography>
                    <Typography variant="h3" fontWeight="700">
                      {totalLeads}
                    </Typography>
                  </Box>
                  <Assignment sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              whileHover={{ scale: 1.05 }}
              sx={{
                background: GRADIENTS.coin,
                color: 'white',
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Enrolled Students
                    </Typography>
                    <Typography variant="h3" fontWeight="700">
                      {dashboardData.totalEnrolled || 0}
                    </Typography>
                  </Box>
                  <People sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              whileHover={{ scale: 1.05 }}
              sx={{
                background: GRADIENTS.navyLight,
                color: 'white',
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Upcoming Batches
                    </Typography>
                    <Typography variant="h3" fontWeight="700">
                      {dashboardData.upcomingBatchesCount || 0}
                    </Typography>
                  </Box>
                  <EventNote sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <MotionCard
              whileHover={{ scale: 1.05 }}
              sx={{
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white',
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Active Plans
                    </Typography>
                    <Typography variant="h3" fontWeight="700">
                      {dashboardData.activePlansCount || 0}
                    </Typography>
                  </Box>
                  <School sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </MotionCard>
          </Grid>
        </Grid>

        {/* Lead Status Breakdown */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={8}>
            <MotionPaper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Lead Status Overview
              </Typography>
              <Grid container spacing={2} mt={1}>
                {Object.entries(stats).map(([status, count]) => (
                  <Grid item xs={6} sm={4} key={status}>
                    <Box textAlign="center" p={2} borderRadius={2} bgcolor="grey.50">
                      <Typography variant="h4" fontWeight="700" color="primary">
                        {count}
                      </Typography>
                      <Typography variant="caption" textTransform="capitalize">
                        {status.replace('_', ' ')}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </MotionPaper>
          </Grid>

          <Grid item xs={12} md={4}>
            <MotionPaper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Quick Actions
              </Typography>
              <Stack spacing={2} mt={2}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setCreateBatchOpen(true)}
                  fullWidth
                >
                  Create New Batch
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setCreatePlanOpen(true)}
                  fullWidth
                >
                  Create New Plan
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Settings />}
                  onClick={openEditInstituteDialog}
                  fullWidth
                >
                  Edit Institute
                </Button>
              </Stack>
            </MotionPaper>
          </Grid>
        </Grid>

        {/* Recent Leads */}
        <MotionPaper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="700" gutterBottom>
            Recent Student Queries
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Student</strong></TableCell>
                  <TableCell><strong>Contact</strong></TableCell>
                  <TableCell><strong>Plan</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(dashboardData.recentLeads || []).slice(0, 5).map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar>{lead.student?.name?.charAt(0)}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {lead.student?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lead.student?.city}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{lead.student?.email}</Typography>
                      <Typography variant="caption">{lead.student?.phone}</Typography>
                    </TableCell>
                    <TableCell>{lead.plan?.name || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={lead.status}
                        size="small"
                        color={
                          lead.status === 'enrolled' ? 'success' :
                          lead.status === 'interested' ? 'primary' :
                          lead.status === 'contacted' ? 'info' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(lead.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => openLeadDetail(lead)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(dashboardData.recentLeads?.length || 0) === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No recent leads. Students can find your institute and send queries from the student portal.
            </Alert>
          )}
        </MotionPaper>
      </motion.div>
    );
  };

  // Render Batches Management
  const renderBatches = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom>
            Batch Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage batches for your institute
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateBatchOpen(true)}
        >
          Create Batch
        </Button>
      </Box>

      <MotionPaper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Batch Name</strong></TableCell>
                <TableCell><strong>Course Type</strong></TableCell>
                <TableCell><strong>Start Date</strong></TableCell>
                <TableCell><strong>Mode</strong></TableCell>
                <TableCell><strong>Capacity</strong></TableCell>
                <TableCell><strong>Price</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Alert severity="info">No batches found. Create your first batch to get started!</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                batches.map((batch) => (
                  <TableRow key={batch.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {batch.name}
                      </Typography>
                      {batch.faculty_name && (
                        <Typography variant="caption" color="text.secondary">
                          Faculty: {batch.faculty_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={batch.course_type} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {new Date(batch.start_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={batch.mode}
                        size="small"
                        color={batch.mode === 'online' ? 'info' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {batch.enrolled_students || 0} / {batch.max_students || '∞'}
                    </TableCell>
                    <TableCell>
                      {batch.price_cents ? `₹${batch.price_cents}` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={batch.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={batch.active ? 'success' : 'default'}
                        icon={batch.active ? <CheckCircle /> : <Cancel />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditBatchDialog(batch)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBatch(batch.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MotionPaper>
    </motion.div>
  );

  // Render Leads Management
  const renderLeads = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box mb={4}>
        <Typography variant="h4" fontWeight="800" gutterBottom>
          Student Queries & Leads
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage student inquiries and track enrollment
        </Typography>
      </Box>

      <MotionPaper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Student</strong></TableCell>
                <TableCell><strong>Contact</strong></TableCell>
                <TableCell><strong>Plan Interested</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Submitted</strong></TableCell>
                <TableCell><strong>Follow Up</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Alert severity="info">No leads yet. Students can find your institute in the student portal!</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {lead.student?.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {lead.student?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lead.student?.city} {lead.student?.age ? `• Age ${lead.student.age}` : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                          <Email fontSize="small" color="action" /> {lead.student?.email}
                        </Typography>
                        {lead.student?.phone && (
                          <Typography variant="caption" display="flex" alignItems="center" gap={0.5}>
                            <Phone fontSize="small" color="action" /> {lead.student.phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {lead.plan ? (
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {lead.plan.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lead.plan.course_type}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption">General Inquiry</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={lead.status}
                          onChange={(e) => handleUpdateLead(lead.id, { status: e.target.value })}
                          displayEmpty
                        >
                          {leadStatuses.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status.replace('_', ' ')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      {new Date(lead.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {lead.follow_up_date ? (
                        <Chip
                          label={new Date(lead.follow_up_date).toLocaleDateString()}
                          size="small"
                          icon={<Schedule />}
                          color="warning"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not set
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => openLeadDetail(lead)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MotionPaper>
    </motion.div>
  );

  // Render Plans Management
  const renderPlans = () => (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="800" gutterBottom>
            Course Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage course plans and pricing
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreatePlanOpen(true)}
        >
          Create Plan
        </Button>
      </Box>

      <Grid container spacing={3}>
        {plans.length === 0 ? (
          <Grid item xs={12}>
            <Alert severity="info">No plans found. Create your first plan to get started!</Alert>
          </Grid>
        ) : (
          plans.map((plan) => (
            <Grid item xs={12} md={6} lg={4} key={plan.id}>
              <MotionCard whileHover={{ scale: 1.02 }} sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box>
                      <Typography variant="h6" fontWeight="700">
                        {plan.name}
                      </Typography>
                      <Chip label={plan.course_type} size="small" color="primary" sx={{ mt: 1 }} />
                    </Box>
                    <Chip
                      label={plan.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={plan.active ? 'success' : 'default'}
                    />
                  </Box>

                  {plan.description && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {plan.description}
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Price
                      </Typography>
                      <Typography variant="body2" fontWeight="700">
                        ₹{plan.price_cents}
                      </Typography>
                    </Box>
                    {plan.duration_months && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Duration
                        </Typography>
                        <Typography variant="body2">
                          {plan.duration_months} months
                        </Typography>
                      </Box>
                    )}
                    {plan.batch_size && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Batch Size
                        </Typography>
                        <Typography variant="body2">
                          {plan.batch_size} students
                        </Typography>
                      </Box>
                    )}
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Mode
                      </Typography>
                      <Chip label={plan.mode} size="small" />
                    </Box>
                  </Box>

                  <Box display="flex" gap={1} mt={3}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => openEditPlanDialog(plan)}
                      fullWidth
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDeletePlan(plan.id)}
                      fullWidth
                    >
                      Delete
                    </Button>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          ))
        )}
      </Grid>
    </motion.div>
  );

  // Render Institute Settings
  const renderSettings = () => {
    if (!institute) return <LinearProgress />;

    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Institute Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your institute information
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={openEditInstituteDialog}
          >
            Edit Details
          </Button>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <MotionPaper sx={{ p: 4 }}>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Institute Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Institute Name
                  </Typography>
                  <Typography variant="h6" fontWeight="600">
                    {institute.name}
                  </Typography>
                </Grid>

                {institute.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {institute.description}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body1" display="flex" alignItems="start" gap={1}>
                    <LocationOn fontSize="small" color="action" />
                    {institute.address}
                    {institute.city && `, ${institute.city}`}
                  </Typography>
                </Grid>

                {institute.contact_email && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Contact Email
                    </Typography>
                    <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                      <Email fontSize="small" color="action" />
                      {institute.contact_email}
                    </Typography>
                  </Grid>
                )}

                {institute.contact_phone && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Contact Phone
                    </Typography>
                    <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                      <Phone fontSize="small" color="action" />
                      {institute.contact_phone}
                    </Typography>
                  </Grid>
                )}

                {institute.established_year && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Established Year
                    </Typography>
                    <Typography variant="body1">
                      {institute.established_year}
                    </Typography>
                  </Grid>
                )}

                {institute.website && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Website
                    </Typography>
                    <Typography variant="body1">
                      <a href={institute.website} target="_blank" rel="noopener noreferrer">
                        {institute.website}
                      </a>
                    </Typography>
                  </Grid>
                )}

                {institute.courses_offered && institute.courses_offered.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Courses Offered
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {institute.courses_offered.map((course, idx) => (
                        <Chip key={idx} label={course} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}

                {institute.facilities && institute.facilities.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Facilities
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {institute.facilities.map((facility, idx) => (
                        <Chip key={idx} label={facility} size="small" />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </MotionPaper>
          </Grid>

          <Grid item xs={12} md={4}>
            <MotionPaper sx={{ p: 3, bgcolor: 'primary.main', color: 'white' }}>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Status
              </Typography>
              <Chip
                label={institute.active ? 'Active' : 'Inactive'}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontWeight: 700,
                  mb: 3,
                }}
              />

              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                Manager
              </Typography>
              <Typography variant="body1" fontWeight="600" mb={3}>
                {institute.manager?.name}
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                Created On
              </Typography>
              <Typography variant="body1" fontWeight="600">
                {new Date(institute.created_at).toLocaleDateString()}
              </Typography>
            </MotionPaper>
          </Grid>
        </Grid>

        {/* Photo Gallery Section */}
        <MotionPaper sx={{ p: 4, mt: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <PhotoLibrary color="primary" fontSize="large" />
              <Box>
                <Typography variant="h6" fontWeight="700">
                  Photo Gallery
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add photos to showcase your institute to students
                </Typography>
              </Box>
            </Box>
            <Box>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                id="institute-image-upload"
              />
              <label htmlFor="institute-image-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={uploadingImages ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                  disabled={uploadingImages}
                >
                  {uploadingImages ? 'Uploading...' : 'Upload Images'}
                </Button>
              </label>
            </Box>
          </Box>

          {gallery.length === 0 ? (
            <Box
              textAlign="center"
              py={6}
              sx={{
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 3,
                bgcolor: 'grey.50',
              }}
            >
              <PhotoLibrary sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No images uploaded yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload images to showcase your institute, classrooms, and facilities
              </Typography>
            </Box>
          ) : (
            <ImageList cols={4} gap={12}>
              {gallery.map((imageUrl, index) => (
                <ImageListItem key={index} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <img
                    src={getImageUrl(imageUrl)}
                    alt={`Institute ${index + 1}`}
                    loading="lazy"
                    style={{ height: 200, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <ImageListItemBar
                    actionIcon={
                      <IconButton
                        sx={{ color: 'white' }}
                        onClick={() => handleDeleteImage(imageUrl)}
                      >
                        <Delete />
                      </IconButton>
                    }
                    sx={{
                      background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Tip: Upload high-quality images of your classrooms, labs, library, and facilities. Students will see these on your institute page.
          </Typography>
        </MotionPaper>
      </motion.div>
    );
  };

  // Render Content
  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return renderDashboard();
      case 'batches':
        return renderBatches();
      case 'leads':
        return renderLeads();
      case 'plans':
        return renderPlans();
      case 'settings':
        return renderSettings();
      default:
        return renderDashboard();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  const drawerWidth = 280;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'transparent' }}>
      <CssBaseline />

      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={(theme) => ({
          ...auroraTopbarSx(theme),
          zIndex: theme.zIndex.drawer + 1,
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        })}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700, color: 'primary.main' }}>
            {institute?.name || 'Institute Manager'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.name || user?.email}
            </Typography>
            <IconButton
              onClick={handleUserMenuOpen}
              size="small"
              sx={{ ml: 1 }}
              aria-controls={anchorEl ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={anchorEl ? 'true' : undefined}
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                {user?.name?.charAt(0) || <AccountCircle />}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight="600">
                {user?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main', mt: 1 }}>
              <ListItemIcon>
                <Logout fontSize="small" color="error" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileMenuOpen : true}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'white',
            pt: 10,
          },
        }}
      >
        <Box sx={{ px: 2, py: 3 }}>
          <Typography variant="h6" fontWeight="700" gutterBottom>
            Institute Manager
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {institute?.name}
          </Typography>
        </Box>
        <Divider />
        <List>
          {menuItems.map((item) => (
            <ListItem
              button
              key={item.id}
              selected={currentView === item.id}
              onClick={() => handleMenuClick(item.id)}
              sx={{
                mb: 0.5,
                mx: 1,
                borderRadius: 2,
                '&.Mui-selected': {
                  bgcolor: 'primary.soft',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.soft',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 4, pt: 12 }}>
        <Container maxWidth="xl">{renderContent()}</Container>
      </Box>

      {/* Create Batch Dialog */}
      <Dialog open={createBatchOpen} onClose={() => setCreateBatchOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">
            Create New Batch
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Batch Name"
                value={newBatch.name}
                onChange={(e) => setNewBatch({ ...newBatch, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Course Type"
                value={newBatch.course_type}
                onChange={(e) => setNewBatch({ ...newBatch, course_type: e.target.value })}
                required
              >
                {COURSE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Mode"
                value={newBatch.mode}
                onChange={(e) => setNewBatch({ ...newBatch, mode: e.target.value })}
              >
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={newBatch.start_date}
                onChange={(e) => setNewBatch({ ...newBatch, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={newBatch.end_date}
                onChange={(e) => setNewBatch({ ...newBatch, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Timings (e.g., 9:00 AM - 12:00 PM)"
                value={newBatch.timings}
                onChange={(e) => setNewBatch({ ...newBatch, timings: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={newBatch.location}
                onChange={(e) => setNewBatch({ ...newBatch, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Students"
                value={newBatch.max_students}
                onChange={(e) => setNewBatch({ ...newBatch, max_students: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Price (₹)"
                value={newBatch.price_cents}
                onChange={(e) => setNewBatch({ ...newBatch, price_cents: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Faculty Name"
                value={newBatch.faculty_name}
                onChange={(e) => setNewBatch({ ...newBatch, faculty_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Registration Deadline"
                value={newBatch.registration_deadline}
                onChange={(e) => setNewBatch({ ...newBatch, registration_deadline: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="YouTube Video URL"
                placeholder="https://www.youtube.com/watch?v=..."
                value={newBatch.video_url}
                onChange={(e) => setNewBatch({ ...newBatch, video_url: e.target.value })}
                InputProps={{
                  startAdornment: <YouTube sx={{ mr: 1, color: 'error.main' }} />,
                }}
                helperText="Add a YouTube video link for batch introduction or demo class"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateBatchOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateBatch} variant="contained">
            Create Batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Batch Dialog - Similar structure */}
      <Dialog open={editBatchOpen} onClose={() => setEditBatchOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">
            Edit Batch
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Batch Name"
                value={editBatchData.name || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Course Type"
                value={editBatchData.course_type || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, course_type: e.target.value })}
              >
                {COURSE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Mode"
                value={editBatchData.mode || 'offline'}
                onChange={(e) => setEditBatchData({ ...editBatchData, mode: e.target.value })}
              >
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={editBatchData.start_date || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={editBatchData.end_date || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Timings"
                value={editBatchData.timings || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, timings: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={editBatchData.location || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Students"
                value={editBatchData.max_students || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, max_students: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Price (₹)"
                value={editBatchData.price_cents || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, price_cents: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Faculty Name"
                value={editBatchData.faculty_name || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, faculty_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="YouTube Video URL"
                placeholder="https://www.youtube.com/watch?v=..."
                value={editBatchData.video_url || ''}
                onChange={(e) => setEditBatchData({ ...editBatchData, video_url: e.target.value })}
                InputProps={{
                  startAdornment: <YouTube sx={{ mr: 1, color: 'error.main' }} />,
                }}
                helperText="Add a YouTube video link for batch introduction or demo class"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditBatchOpen(false)}>Cancel</Button>
          <Button onClick={handleEditBatch} variant="contained">
            Update Batch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Plan Dialog */}
      <Dialog open={createPlanOpen} onClose={() => setCreatePlanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">
            Create Course Plan
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Name"
                value={newPlan.name}
                onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={newPlan.description}
                onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Course Type"
                value={newPlan.course_type}
                onChange={(e) => setNewPlan({ ...newPlan, course_type: e.target.value })}
                required
              >
                {COURSE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Mode"
                value={newPlan.mode}
                onChange={(e) => setNewPlan({ ...newPlan, mode: e.target.value })}
              >
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Price (₹)"
                value={newPlan.price_cents}
                onChange={(e) => setNewPlan({ ...newPlan, price_cents: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Duration (months)"
                value={newPlan.duration_months}
                onChange={(e) => setNewPlan({ ...newPlan, duration_months: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Batch Size"
                value={newPlan.batch_size}
                onChange={(e) => setNewPlan({ ...newPlan, batch_size: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreatePlanOpen(false)}>Cancel</Button>
          <Button onClick={handleCreatePlan} variant="contained">
            Create Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog - Similar to create */}
      <Dialog open={editPlanOpen} onClose={() => setEditPlanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">
            Edit Course Plan
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plan Name"
                value={editPlanData.name || ''}
                onChange={(e) => setEditPlanData({ ...editPlanData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={editPlanData.description || ''}
                onChange={(e) => setEditPlanData({ ...editPlanData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Course Type"
                value={editPlanData.course_type || ''}
                onChange={(e) => setEditPlanData({ ...editPlanData, course_type: e.target.value })}
              >
                {COURSE_OPTIONS.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Mode"
                value={editPlanData.mode || 'offline'}
                onChange={(e) => setEditPlanData({ ...editPlanData, mode: e.target.value })}
              >
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="hybrid">Hybrid</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Price (₹)"
                value={editPlanData.price_cents || ''}
                onChange={(e) => setEditPlanData({ ...editPlanData, price_cents: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Duration (months)"
                value={editPlanData.duration_months || ''}
                onChange={(e) => setEditPlanData({ ...editPlanData, duration_months: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Batch Size"
                value={editPlanData.batch_size || ''}
                onChange={(e) => setEditPlanData({ ...editPlanData, batch_size: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditPlanOpen(false)}>Cancel</Button>
          <Button onClick={handleEditPlan} variant="contained">
            Update Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={leadDetailOpen} onClose={() => setLeadDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">
            Lead Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Box mt={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                      {selectedLead.student?.name?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="600">
                        {selectedLead.student?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedLead.student?.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">{selectedLead.student?.phone || 'N/A'}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    City
                  </Typography>
                  <Typography variant="body1">{selectedLead.student?.city || 'N/A'}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Age
                  </Typography>
                  <Typography variant="body1">{selectedLead.student?.age || 'N/A'}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Chip label={selectedLead.status} size="small" color="primary" />
                </Grid>

                {selectedLead.plan && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Interested In
                    </Typography>
                    <Typography variant="body1" fontWeight="600">
                      {selectedLead.plan.name} - {selectedLead.plan.course_type}
                    </Typography>
                  </Grid>
                )}

                {selectedLead.student?.interested_courses && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Interested Courses
                    </Typography>
                    <Typography variant="body1">{selectedLead.student.interested_courses}</Typography>
                  </Grid>
                )}

                {selectedLead.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1">{selectedLead.notes}</Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Submitted On
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedLead.created_at).toLocaleString()}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Add Notes"
                    defaultValue={selectedLead.notes || ''}
                    helperText="Update notes about this lead"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Follow-up Date"
                    defaultValue={selectedLead.follow_up_date?.split('T')[0] || ''}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setLeadDetailOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              // Handle update
              setLeadDetailOpen(false);
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Institute Dialog */}
      <Dialog open={editInstituteOpen} onClose={() => setEditInstituteOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="700">
            Edit Institute Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Institute Name"
                value={editInstituteData.name || ''}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={editInstituteData.description || ''}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Address"
                value={editInstituteData.address || ''}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={INDIAN_CITIES}
                value={editInstituteData.city || ''}
                onChange={(e, newValue) => setEditInstituteData({ ...editInstituteData, city: newValue || '' })}
                onInputChange={(e, newValue) => setEditInstituteData({ ...editInstituteData, city: newValue || '' })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="City"
                    placeholder="Select or type city"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Established Year"
                value={editInstituteData.established_year || ''}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, established_year: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={COURSE_OPTIONS}
                value={editInstituteData.courses_offered || []}
                onChange={(e, newValue) => setEditInstituteData({ ...editInstituteData, courses_offered: newValue })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Courses Offered"
                    placeholder="Select courses (JEE, NEET, UPSC, etc.)"
                    helperText="Students will see your institute based on their course interests"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      color="primary"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Contact Email"
                value={editInstituteData.contact_email || ''}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, contact_email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={editInstituteData.contact_phone || ''}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, contact_phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Website"
                value={editInstituteData.website || ''}
                onChange={(e) => setEditInstituteData({ ...editInstituteData, website: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditInstituteOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateInstitute} variant="contained">
            Update Institute
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default InstituteManagerDashboard;
